import Loader from "components/loader"; // Adjust the import path as necessary
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import {
  addAdmin,
  addVideoCategory,
  getCategoryById,
  updateCategory,
} from "../../axios/api"; // Adjust the import path as necessary
import { encryptData } from "utils/utils";

const generatePassword = () => {
  const minLength = 8;
  const lowercaseCharset = "abcdefghijklmnopqrstuvwxyz";
  const uppercaseCharset = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const numberCharset = "0123456789";
  const symbolCharset = '!@#$%^&*(),.?":{}|<>';

  let password = "";

  // Ensure at least one of each required character type
  password += lowercaseCharset.charAt(
    Math.floor(Math.random() * lowercaseCharset.length)
  );
  password += uppercaseCharset.charAt(
    Math.floor(Math.random() * uppercaseCharset.length)
  );
  password += numberCharset.charAt(
    Math.floor(Math.random() * numberCharset.length)
  );
  password += symbolCharset.charAt(
    Math.floor(Math.random() * symbolCharset.length)
  );

  // Fill the rest of the password with random characters from all charsets
  const remainingLength = minLength - password.length;
  const charset =
    lowercaseCharset + uppercaseCharset + numberCharset + symbolCharset;
  for (let i = 0; i < remainingLength; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }

  // Shuffle the password to randomize character positions
  password = password
    .split("")
    .sort(() => Math.random() - 0.5)
    .join("");
  return password;
};

const AddAdmin = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState<any>();
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: "",
      name: "",
      default_password_hash: "",
      role: "admin",
    },
    onSubmit: async (values) => {
      // console.log("🚀 ~ onSubmit: ~ values:", values);
      try {
        setLoading(true);
        let responseData;
        if (params?.id) {
          responseData = await updateCategory(values, params?.id);
          if (responseData?.data?.success) {
            toast.success("Category Updated Successfully");
            navigate("/admin/video-category");
          } else {
            toast.error("Something Went Wrong");
          }
        } else {
          // Encrypt password to send it on backend
          values.default_password_hash = encryptData(
            values.default_password_hash
          );
          responseData = await addAdmin(values);
          // console.log("🚀 ~ onSubmit: ~ responseData:", responseData);
          if (responseData?.success) {
            toast.success("Admin Added Successfully");
            navigate("/admin/all-admins");
          } else {
            toast.error(responseData?.message);
          }
        }
        // console.log("API Response:", responseData);
      } catch (error) {
        console.error("API Error:", error);
        toast.error("Something Went Wrong");
      } finally {
        setLoading(false);
      }
    },
    validationSchema: yup.object({
      email: yup.string().required("email is required"),
      name: yup.string().required("Name is required"),
      default_password_hash: yup
        .string()
        .required("Required")
        .min(8, "Password must be at least 8 characters long")
        .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
        .matches(/[a-z]/, "Password must contain at least one lowercase letter")
        .matches(/[0-9]/, "Password must contain at least one number")
        .matches(
          /[!@#$%^&*(),.?":{}|<>]/,
          "Password must contain at least one special character"
        ),
      role: yup.string().required("Role is required"),
    }),
  });
  useEffect(() => {
    const fetchCategory = async (id: string) => {
      try {
        setLoading(true);
        const categoryDataResp = await getCategoryById(id);
        setCategory(categoryDataResp?.data?.data);
      } catch (error) {
        console.log("error", error);
      } finally {
        setLoading(false);
      }
    };
    if (params?.id) {
      fetchCategory(params?.id);
    }
  }, [params]);

  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    formik.setFieldValue("default_password_hash", newPassword);
  };

  //   useEffect(() => {
  //     if (category) {
  //       formik.setValues({
  //         title: category.title,
  //       });
  //     }
  //   }, [category]);

  return (
    <div className="m-3 bg-[#FFFFFF] p-3">
      {loading ? (
        <Loader />
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <div className="my-5 flex w-11/12 flex-col">
            <div className="my-2 flex w-[61%] flex-col items-start justify-center gap-2">
              <label htmlFor="email" className="text-xl font-medium">
                Email
              </label>
              <input
                type="email"
                name="email"
                id="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter Admins Email here"
                className="h-10 w-[100%] rounded-md border-[2px] border-[#E4E6EF] bg-[transparent] p-2"
                autoComplete="off"
              />
              {formik.touched.email && formik.errors.email ? (
                <div className="text-lg text-red-500">
                  {formik.errors.email}
                </div>
              ) : null}
              <label htmlFor="name" className="text-xl font-medium">
                UserName
              </label>
              <input
                type="text"
                name="name"
                id="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter Admin Username here"
                autoComplete="off"
                className="h-10 w-[100%] rounded-md border-[2px] border-[#E4E6EF] bg-[transparent] p-2"
              />
              {formik.touched.name && formik.errors.name ? (
                <div className="text-lg text-red-500">{formik.errors.name}</div>
              ) : null}
              <label
                htmlFor="default_password_hash"
                className="text-xl font-medium"
              >
                Password
              </label>
              <input
                type="text"
                name="default_password_hash"
                id="default_password_hash"
                value={formik.values.default_password_hash}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter Admin Password here"
                autoComplete="off"
                className="h-10 w-[100%] rounded-md border-[2px] border-[#E4E6EF] bg-[transparent] p-2"
              />
              <button
                type="button"
                onClick={handleGeneratePassword}
                className="w-27 float-right my-3 rounded bg-[#008BD2] p-2 text-xl font-bold text-white"
              >
                Generate
              </button>
              {formik.touched.default_password_hash &&
              formik.errors.default_password_hash ? (
                <div className="text-lg text-red-500">
                  {formik.errors.default_password_hash}
                </div>
              ) : null}
              <label htmlFor="role" className="text-xl font-medium">
                Role: Admin
              </label>
              {/* <select
                name="role"
                id="role"
                value={formik.values.role}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="h-10 w-[100%] rounded-md border-[2px] border-[#E4E6EF] bg-[transparent] px-2 text-xl"
              >
                <option
                  value={"superAdmin"}
                  label={"SuperAdmin"}
                  className="text-xl font-medium"
                />
                <option
                  value={"admin"}
                  label={"Admin"}
                  className="text-xl font-medium"
                />
              </select>
              {formik.touched.role && formik.errors.role ? (
                <div className="text-lg text-red-500">{formik.errors.role}</div>
              ) : null} */}
              <div>
                <button
                  name="submit"
                  className="float-right my-5 w-24 rounded bg-[#008BD2] p-2 text-xl font-bold text-white"
                  type="submit"
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default AddAdmin;
